/** Seed catalogue of the demo's rules-engine edge cases (see AGENTS/demo plan). Each scenario
 * is data-driven so the story can be narrated even where a screen doesn't yet live-evaluate it.
 * `demoWired: true` scenarios are actually evaluated live by `src/utils/rulesEngine.ts` and
 * surfaced via `ExceptionBanner`; the rest stay as seeded/narratable content (e.g. on the
 * AutomatedWorkflows screen) to protect the demo timeline. */

export type RuleCategory = 'Supplier' | 'Catalogue' | 'Order' | 'Delivery' | 'Waste' | 'Margin';
export type RuleSeverity = 'Info' | 'Warning' | 'Blocking';

export interface RuleScenario {
  code: string;
  title: string;
  description: string;
  category: RuleCategory;
  severity: RuleSeverity;
  demoWired: boolean;
}

export const rulesEngineScenarios: RuleScenario[] = [
  {
    code: 'NEW_SUPPLIER_FULL_RECEIPT_COUNT',
    title: 'New supplier forces full receipt count',
    description: 'First deliveries from a newly onboarded supplier require a full goods-in count rather than a spot check.',
    category: 'Supplier',
    severity: 'Warning',
    demoWired: true,
  },
  {
    code: 'EXPIRED_CERT_BLOCKS_ORDERING',
    title: 'Expired supplier certification blocks ordering',
    description: 'Orders cannot be placed with a supplier whose food safety certification has expired.',
    category: 'Supplier',
    severity: 'Blocking',
    demoWired: true,
  },
  {
    code: 'MISSING_ALLERGEN_BLOCKS_LAUNCH',
    title: 'Missing allergen data blocks launch',
    description: 'A new product cannot go live/trial until allergen data is complete.',
    category: 'Catalogue',
    severity: 'Blocking',
    demoWired: true,
  },
  {
    code: 'PRICE_CHANGE_NEEDS_APPROVAL',
    title: 'Price change needs approval',
    description: 'A supplier price increase is held pending commercial approval before it applies.',
    category: 'Catalogue',
    severity: 'Warning',
    demoWired: false,
  },
  {
    code: 'OVER_BUDGET_NEEDS_APPROVAL',
    title: 'Over-budget order goes for approval',
    description: 'An order exceeding the remaining site budget is routed to head office for approval.',
    category: 'Order',
    severity: 'Blocking',
    demoWired: true,
  },
  {
    code: 'OFF_CONTRACT_ITEM_FLAGGED',
    title: 'Off-contract item gets flagged',
    description: 'An item ordered outside the agreed catalogue/contract is flagged for review.',
    category: 'Order',
    severity: 'Warning',
    demoWired: true,
  },
  {
    code: 'UNUSUAL_VOLUME_FLAGGED',
    title: 'Unusual order volume gets flagged',
    description: 'An order quantity significantly above the historical average is flagged for manager review.',
    category: 'Order',
    severity: 'Warning',
    demoWired: false,
  },
  {
    code: 'SHORT_DELIVERY_CREDIT_NOTE',
    title: 'Short delivery triggers credit note by exception',
    description: 'A delivery received short of the ordered quantity automatically raises a credit note exception.',
    category: 'Delivery',
    severity: 'Warning',
    demoWired: true,
  },
  {
    code: 'LATE_DELIVERY_IMPACTS_SCORE',
    title: 'Late supplier delivery impacts supplier score',
    description: 'A delivery received after its scheduled window reduces the supplier performance score.',
    category: 'Delivery',
    severity: 'Info',
    demoWired: false,
  },
  {
    code: 'PARTIAL_DELIVERY_UPDATES_FORECAST',
    title: 'Partial delivery updates expected stock/P&L',
    description: 'A supplier sending only part of an order (e.g. 80%) updates expected stock cover and forecast P&L.',
    category: 'Delivery',
    severity: 'Warning',
    demoWired: true,
  },
  {
    code: 'HIGH_WASTE_CREATES_ACTION',
    title: 'High waste creates an action/task',
    description: 'Waste recorded above the site threshold automatically creates a follow-up task for the manager.',
    category: 'Waste',
    severity: 'Warning',
    demoWired: true,
  },
  {
    code: 'LOW_MARGIN_ESCALATED',
    title: 'Low margin risk gets escalated',
    description: 'A product or order trending below the target margin threshold is escalated to commercial.',
    category: 'Margin',
    severity: 'Warning',
    demoWired: false,
  },
];

export function getRuleScenario(code: string): RuleScenario | undefined {
  return rulesEngineScenarios.find((scenario) => scenario.code === code);
}
